/**
 * Navigations-Registry — die **einzige** Quelle für alle Menüdarstellungen.
 *
 * Sidebar (ab Tablet-Breite), schwebende Tab-Leiste (mobil) und die
 * ⌘K-Palette lesen alle hier. Der Grund steht in `apple-struktur.md`: Drei
 * getrennt gepflegte Menüs driften auseinander — ein Punkt wird in der
 * Sidebar ergänzt, in der Tab-Leiste vergessen, und mobil ist die Seite
 * unerreichbar, ohne dass es jemandem auffällt.
 *
 * Die bisherigen acht Admin-Punkte sind auf **fünf Bereiche** verdichtet;
 * mehr trägt eine Tab-Leiste nicht. Verwandtes liegt beieinander: Die
 * API-Historie gehört zu den Analysen, Designs und Query-Profile zur
 * Konfiguration.
 */
import type de from '@/i18n/messages/de.json';
import type { UserRole } from '@/types';
import { hasMinRole } from '@/utils/roles';
import {
  LayoutDashboard,
  FileSearch,
  Users,
  SlidersHorizontal,
  Settings,
  type LucideIcon,
} from 'lucide-react';

/**
 * Schlüssel im `nav`-Namensraum. Als Union statt als loser `string`, damit ein
 * Tippfehler beim Ergänzen eines Bereichs sofort auffällt und nicht erst als
 * roher Schlüssel in der Oberfläche steht.
 */
export type NavLabelKey = `nav.${keyof (typeof de)['nav'] & string}`;

export interface NavZiel {
  id: string;
  /** Schlüssel in den Übersetzungen, nie ein fertiger Text. */
  labelKey: NavLabelKey;
  href: string;
  /**
   * Kleinste Rolle, der das Ziel angeboten wird; ohne Angabe jeder angemeldeten
   * Rolle. **Rein kosmetisch:** Das Backend entscheidet, ob der Aufruf gelingt
   * (`middleware/keycloakAuth.ts`). Hier geht es nur darum, keinen Eintrag zu
   * zeigen, der zu einer Seite führt, die nur 403 meldet.
   */
  mindestrolle?: UserRole;
}

export interface Bereich {
  id: string;
  labelKey: NavLabelKey;
  icon: LucideIcon;
  /** Das Ziel, das der Bereichswechsel anspringt — das erste seiner Ziele. */
  href: string;
  ziele: NavZiel[];
}

export const BEREICHE: readonly Bereich[] = [
  {
    id: 'overview',
    labelKey: 'nav.overview',
    icon: LayoutDashboard,
    href: '/admin',
    ziele: [
      { id: 'dashboard', labelKey: 'nav.dashboard', href: '/admin' },
      // Die Nutzung nennt Kunden, Kosten und Summen aller Mandanten — das Backend
      // lässt jede Route unter /api/v1/usage nur den GLOBAL_ADMIN lesen.
      { id: 'usage', labelKey: 'nav.usage', href: '/admin/usage', mindestrolle: 'GLOBAL_ADMIN' },
    ],
  },
  {
    id: 'analyses',
    labelKey: 'nav.analyses',
    icon: FileSearch,
    href: '/admin/analyses',
    ziele: [
      { id: 'analyses', labelKey: 'nav.analyses', href: '/admin/analyses' },
      // Der Standortvergleich (S-09) ist eine Analyse mit mehreren Adressen —
      // er gehoert hierher, nicht in einen sechsten Bereich.
      { id: 'vergleich', labelKey: 'nav.vergleich', href: '/admin/vergleich' },
      // Die API-Historie trägt E-Mail und Adresse der Anfragenden aller Mandanten —
      // das Backend lässt jede Route unter /api/v1/api-calls nur den GLOBAL_ADMIN lesen.
      { id: 'apiHistory', labelKey: 'nav.apiHistory', href: '/admin/api-history', mindestrolle: 'GLOBAL_ADMIN' },
    ],
  },
  {
    id: 'customers',
    labelKey: 'nav.customers',
    icon: Users,
    href: '/admin/customers',
    // Die Kundenverwaltung gehört dem GLOBAL_ADMIN — das Backend erzwingt es für
    // jede Route unter /api/v1/customers, auch lesend. Ohne dieses Ziel entfällt
    // der Bereich für alle anderen Rollen ganz.
    ziele: [
      { id: 'customers', labelKey: 'nav.customers', href: '/admin/customers', mindestrolle: 'GLOBAL_ADMIN' },
    ],
  },
  {
    id: 'configuration',
    labelKey: 'nav.configuration',
    icon: SlidersHorizontal,
    href: '/admin/query-profiles',
    ziele: [
      { id: 'branchen', labelKey: 'nav.branchen', href: '/admin/branchen' },
      { id: 'textbausteine', labelKey: 'nav.textbausteine', href: '/admin/textbausteine' },
      { id: 'flags', labelKey: 'nav.flags', href: '/admin/flags' },
      { id: 'pakete', labelKey: 'nav.pakete', href: '/admin/pakete' },
      { id: 'queryProfiles', labelKey: 'nav.queryProfiles', href: '/admin/query-profiles' },
      // Die Design-Verwaltung gehört dem GLOBAL_ADMIN — das Backend erzwingt es
      // für jede Route unter /api/v1/designs, auch lesend.
      { id: 'designs', labelKey: 'nav.designs', href: '/admin/designs', mindestrolle: 'GLOBAL_ADMIN' },
    ],
  },
  // Sekundäres ans Ende — apple-struktur.md.
  {
    id: 'settings',
    labelKey: 'nav.settings',
    icon: Settings,
    href: '/admin/settings',
    ziele: [{ id: 'settings', labelKey: 'nav.settings', href: '/admin/settings' }],
  },
] as const;

/** Alle Ziele über alle Bereiche — unabhängig von der Rolle. Für die Anzeige: `zieleFuerRolle`. */
export function alleZiele(): NavZiel[] {
  return BEREICHE.flatMap((b) => b.ziele);
}

/**
 * Wird das Ziel dieser Rolle angeboten?
 *
 * Ohne bekannte Rolle (die Sitzung ist noch nicht geladen) gilt das
 * Strengere: Eingeschränkte Ziele bleiben verborgen, statt kurz aufzublitzen.
 */
function wirdAngeboten(ziel: NavZiel, rolle: UserRole | undefined): boolean {
  return erfuellt(rolle, ziel.mindestrolle);
}

function erfuellt(rolle: UserRole | undefined, mindestrolle: UserRole | undefined): boolean {
  if (!mindestrolle) return true;
  return rolle !== undefined && hasMinRole(rolle, mindestrolle);
}

/**
 * Die Bereiche, wie sie dieser Rolle gezeigt werden: eingeschränkte Ziele
 * entfallen, ein Bereich ohne verbleibendes Ziel ebenfalls. Zeigt der
 * Bereichslink auf ein entfallenes Ziel, springt er zum ersten verbleibenden.
 */
export function bereicheFuerRolle(rolle: UserRole | undefined): Bereich[] {
  return BEREICHE.flatMap((bereich) => {
    const ziele = bereich.ziele.filter((ziel) => wirdAngeboten(ziel, rolle));
    const erstes = ziele[0];
    if (!erstes) return [];
    const href = ziele.some((ziel) => ziel.href === bereich.href) ? bereich.href : erstes.href;
    return [{ ...bereich, href, ziele }];
  });
}

/** Alle Ziele, die diese Rolle sieht — Grundlage der ⌘K-Palette. */
export function zieleFuerRolle(rolle: UserRole | undefined): NavZiel[] {
  return bereicheFuerRolle(rolle).flatMap((b) => b.ziele);
}

/**
 * Gehört der Pfad zu diesem Ziel — oder zu einer Unterseite davon?
 *
 * `/admin` ist Präfix aller übrigen Pfade. Ohne Sonderbehandlung wäre die
 * Übersicht überall aktiv — deshalb gilt für sie ausschließlich exakte
 * Übereinstimmung, für alle anderen auch der Unterpfad (Detailseiten).
 */
function passtZuPfad(ziel: NavZiel, pfad: string): boolean {
  return ziel.href === '/admin' ? pfad === '/admin' : pfad === ziel.href || pfad.startsWith(`${ziel.href}/`);
}

/**
 * Welche Rolle verlangt dieser Pfad mindestens? Das Ziel, zu dem er gehört
 * (bei mehreren das spezifischste), gibt sie vor; `undefined` heißt: jede.
 * Dieselbe Quelle wie das Menü, damit Menü und Zugang nicht auseinanderlaufen.
 */
export function mindestrolleFuerPfad(pfad: string): UserRole | undefined {
  const treffer = alleZiele().filter((ziel) => passtZuPfad(ziel, pfad));
  return treffer.sort((a, b) => b.href.length - a.href.length)[0]?.mindestrolle;
}

/**
 * Wird dieser Pfad der Rolle angeboten? Für Links außerhalb des Menüs
 * (Schnellaktionen, Verweise), damit kein Knopf auf eine Seite führt, die die
 * Rolle abweist. Suchparameter und Anker gehören nicht zum Pfad. Wie im Menü
 * gilt ohne bekannte Rolle das Strengere — und wie dort rein kosmetisch.
 */
export function wirdPfadAngeboten(pfad: string, rolle: UserRole | undefined): boolean {
  const ohneZusatz = pfad.split(/[?#]/)[0] ?? pfad;
  return erfuellt(rolle, mindestrolleFuerPfad(ohneZusatz));
}

/**
 * Welcher Bereich ist für diesen Pfad aktiv?
 *
 * Gilt für die ganze Registry, nicht nur für das, was eine Rolle sieht.
 */
export function bereichFuerPfad(pfad: string): Bereich | undefined {
  const treffer = BEREICHE.filter((bereich) => bereich.ziele.some((ziel) => passtZuPfad(ziel, pfad)));

  // Bei mehreren Treffern gewinnt der spezifischste (längstes passendes Ziel).
  return treffer.sort((a, b) => laengstesZiel(b, pfad) - laengstesZiel(a, pfad))[0];
}

function laengstesZiel(bereich: Bereich, pfad: string): number {
  return Math.max(...bereich.ziele.map((z) => (pfad.startsWith(z.href) ? z.href.length : 0)));
}
